import React, { useCallback, useEffect, useState, useContext, createContext } from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import Button from 'react-bootstrap/Button';
import './App.css';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { get, post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { MdAccountCircle, MdOutlineAccountCircle } from 'react-icons/md';
import { useWindowDimensions } from './libs/hooksLib';
import ErrorCatcher from './ErrorCatcher';
import { logout } from './libs/authLib';
import { ToastQueue } from './libs/toastLib';
import AccountModal from './AccountModal';
import { SPACES } from './libs/miscLib';
import { SwdProvider } from './libs/autofillLib';
import BrandHomeMenu from './BrandHomeMenu';



const AppContext = createContext(null);

export function useAppContext() {
  return useContext(AppContext);
}


function App() {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isAuthenticated, userHasAuthenticated] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Window size
  const { windowWidth, windowHeight } = useWindowDimensions();


  // Handle authentication and logout

  useEffect(() => {
    onLoad();
  }, []);

  async function onLoad() {
    try {
      const tokens = (await fetchAuthSession()).tokens;
      if (tokens) {
        userHasAuthenticated(true);
      }
    } catch(e) {
      console.log('Error fetching current auth session');
    }

    setIsAuthenticating(false);
  }

  function handleLogout(onLogout) {
    logout({ onSuccess: () => {
      userHasAuthenticated(false);
      if (onLogout) onLogout();
    } });
  }

  // For authenticated users, get and set user preferences (saved to server) as key/value pairs
  const getUserPreference = useCallback(async (key) => {
    if (!key || typeof key !== 'string' || !/^[a-zA-Z][a-zA-Z0-9]*$/.test(key)) {
      console.warn('Returning null user preference because invalid key given: ', key);
      return null;
    }
    if (!isAuthenticated) {
      console.warn('User preferences are only retrievable for authenticated users.')
      return null;
    }

    const resp = await (await get({
      apiName: 'userPreferences',
      path: `/userPreferences/${key}`
    }).response).body.json();
    return resp;
  }, [isAuthenticated]);

  const setUserPreference = useCallback(async (key, value) => {
    if (!key || typeof key !== 'string' || !/^[a-zA-Z][a-zA-Z0-9]*$/.test(key)) {
      console.warn('User preference not set because invalid key given: ', key);
      return false;
    }
    if (!isAuthenticated) {
      console.warn('User preferences are only settable for authenticated users.')
      return false;
    }

    await (await post({
      apiName: 'userPreferences',
      path: `/userPreferences/${key}`,
      options: { body: { value } },
    }).response).body.json();
    return true;
  }, [isAuthenticated]);



  // The website exists across two "spaces": the normal "Crossworthy" and "Crossworthy Construct"
  const currentSpace = (
    location.pathname === '/' || location.pathname === '/about' ? SPACES.SPLASH :
    location.pathname.startsWith('/play') || location.pathname.startsWith('/collections') ? SPACES.PLAY :
    location.pathname.startsWith('/construct') || location.pathname.startsWith('/manage-collections') ? SPACES.CONSTRUCT :
    SPACES.INTERSTITIAL
  );

  // Title and meta tags (mostly to update the document title, and provide descriptions for Googlebot)
  /* Note: 
  I've set this up as a bare-bones "catch-all" helmet, and it's not intended to cover specific bases.
  If there's a good reason, child components can and should override this helmet anytime by simply including their own helmet. 
  For instance, the "About" and "Splash" page components have their own helmets with better descriptions for those pages.
  */

  const generalHelmet = currentSpace === SPACES.CONSTRUCT ? (
    <Helmet>
      <title>Crossworthy Construct</title>
      <meta name='description' content='Make your own crosswords with easy collaboration, sharing, and fill assistance!' />
    </Helmet>
  ) : currentSpace === SPACES.PLAY ? (
    <Helmet>
      <title>Crossworthy Play</title>
      <meta name='description' content='Solve free, indie crosswords on Crossworthy.' />
    </Helmet>
  ) : (
    <Helmet>
      <title>Crossworthy Crosswords</title>
      <meta name='description' content='Make, share, and play crossword puzzles, with easy collaboration and fill assistance. A free and indie site!' />
    </Helmet>
  );


  const [splashNavIsHidden, setSplashNavIsHidden] = useState(false);   // when on the splash/interstitial pages, this value is true when user has scrolled down to the point where it should hide
  const brandIsShowing = windowWidth > 660;    // whether or not the brand logo is showing at all (not on small screens)

  const brand = <BrandHomeMenu currentSpace={currentSpace} />

  const settingsButton = (
    <div>
      {currentSpace === SPACES.SPLASH && windowWidth > 660 ? (
        <div className={'d-flex gap-2 p-1 ' + (splashNavIsHidden ? 'fade-invisible' : 'fade-visible')} style={{ position: 'fixed', zIndex: 200, top: '20px', right: '35px', borderRadius: '20px' }}>
          {isAuthenticated ? (
            <Button
              size='sm'
              style={{ borderRadius: '12px' }}
              variant='secondary'
              href='/construct/f'
              onClick={e => {
                e.preventDefault();
                navigate('/construct/f');
              }}
            >My constructions</Button>
          ) : (
            <>
              <Button
                size='sm'
                style={{ borderRadius: '12px' }}
                variant='secondary'
                onClick={e => {
                  e.preventDefault();
                  setShowSettingsModal(true);
                }}
              >Login/Signup</Button>
            </>
          )}
          {isAuthenticated && <>
            <Button
              size='sm'
              style={{ borderRadius: '12px' }}
              variant='secondary'
              onClick={e => {
                e.preventDefault();
                setShowSettingsModal(true);
              }}
            >Account</Button>
          </>}
          <Button
            size='sm'
            style={{ borderRadius: '12px' }}
            variant='secondary'
            href='/about'
            onClick={e => {
              e.preventDefault();
              navigate('/about');
            }}
          >About Us</Button>
        </div>

      ) : (

        <div role='button' onClick={() => setShowSettingsModal(s => !s)}>
          <div
            className='grey-1-bkgd'
            style={{ position: 'fixed', zIndex: 200, top: '-8px', right: '-5px', height: '50px', width: '50px', borderRadius: '50%', boxShadow: '0 0 5px #3A3B3C' }}
          />
          {isAuthenticated ? (
            <MdAccountCircle
              className='zoomable'
              size={25}
              style={{ position: 'fixed', zIndex: 201, top: '5px', right: '7px' }}
            />
          ) : (
            <MdOutlineAccountCircle
              className='zoomable'
              size={25}
              style={{ position: 'fixed', zIndex: 201, top: '5px', right: '7px' }}
            />
          )}
        </div>

      )}
    </div>
  );

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const settingsModal = <AccountModal show={showSettingsModal} onHide={() => setShowSettingsModal(false)} currentSpace={currentSpace} />;
  const showLoginPage = useCallback(() => setShowSettingsModal(true), []);   // memoization is necessary to avoid infinite render loops (if the function is used in a child component's useEffect)



  const [bottomPadding, setBottomPadding] = useState(0);   // padding added e.g. if mobile keyboard is showing



  return (
    !isAuthenticating && (
      <HelmetProvider>
        <AppContext.Provider value={{
          windowWidth, windowHeight,
          isMobile: windowWidth < 660 || windowHeight < 500,
          isAuthenticated, userHasAuthenticated, handleLogout, showLoginPage,
          getUserPreference, setUserPreference,
          displaySettingsModal: () => setShowSettingsModal(true),
          brandIsShowing, splashNavIsHidden, setSplashNavIsHidden,
          setBottomPadding, bottomPadding,
        }}>
          <ToastQueue>
            <ErrorCatcher>
              <SwdProvider>
                {generalHelmet}
                {brandIsShowing && brand}
                {settingsButton}
                {settingsModal}

                <Outlet />
              </SwdProvider>
            </ErrorCatcher>
          </ToastQueue>

          <div
            id='bottom-spacer'
            style={{ height: bottomPadding, visibility: 'hidden' }}
          />

        </AppContext.Provider>
      </HelmetProvider>
    )
  );
}

export default App;
